import { Box, Flex, Text } from '@chakra-ui/react';
import { useCallback, useEffect, useState } from 'react';
import { scrollToPendingFileField } from './fileReplacementNavigation';
import {
  faChevronLeft,
  faChevronRight,
  faSave
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ajvResolver } from '@hookform/resolvers/ajv';
import { Button, OutlineCard } from '@morressier/alembic';
import { FormProvider, useForm } from 'react-hook-form';
import { ISubmissionFormData, useFormContext } from 'src/contexts/FormContext';
import DataAvailability from './components/DataAvailability';
import ImmediatePublication from './components/ImmediatePublication';
import { generateValidationSchema } from 'src/utils/validation';
import {
  applyRichTextSchemaKeywords,
  RICH_TEXT_AJV_KEYWORDS
} from 'src/utils/richTextSchemaValidation';
import TransparentPeerReview from './components/TransparentPeerReview';
import DoubleAnonymousReview from './components/DoubleAnonymousReview';
import PublishingModel from './components/PublishingModel';
import NoveltyStatement from './components/NoveltyStatement';
import FiguresAndTables from './components/FiguresAndTables';
import ReviewerRecommendation from './components/ReviewerRecommendation';
import EthicsStatement from './components/EthicsStatement';
import ClinicalTrials from './components/ClinicalTrials';
import Funding from './components/Funding';
import AcceleratedPublication from './components/AcceleratedPublication';
import SubjectArea from './components/SubjectArea';
import StepHeader from './components/StepHeader';
import { FormFieldRecommendedReviewers } from '@publishing-engine/domain/types/article';
import RadioGroup from './components/common/RadioGroup';
import TextInput from './components/common/TextInput';
import useCurrentContextId from 'src/hooks/useCurrentContextId';
import LicensesAndPermissionForm from './components/LicensesAndPermissions';
import { useSubmissionContextData } from 'src/contexts/SubmissionContextProvider';
import { scrollToFirstError } from 'src/utils/scrollToFirstError';
import DeleteDraft from './DeleteDraft';
import { checkFileUpload } from '..';
import FundingGeneric from './components/FundingGeneric';
import { CustomFormFieldComponent } from 'src/components/FormField';
import RadioGroupWithTextArea from './components/RadioGroupWithTextArea';
import GeneratedDataset from './components/GeneratedDataset';
import UtilizedDataset from './components/UtilizedDataset';
import OpenAccessOnly from './components/OpenAccessOnly';
import AuthorDeclaration from './components/AuthorDeclaration';
import AITools from './components/AITools';
import { IPreferenceStepConfig } from 'src/hooks/useSubmissionStepsConfig';
import { SidebarLayout } from 'src/layouts/SidebarLayout';
import { SubmissionSidebar } from './SubmissionSidebar';
import { FieldLabel } from 'src/components/Field';
import { hasDuplicateFundingBody } from 'src/utils/hasDuplicateFundingBody';
import DataAvailabilityStatement from './components/DataAvailabilityStatement';
import { useSubmissionLeaveGuardRegistration } from './SubmissionLeaveGuard';
import GenericRichTextInput from './components/common/GenericRichTextInput';
interface Props {
  currentStep: number;
  totalSteps: number;
  draftSubmissionId: string;
  goToPreviousStep: () => void;
  goToNextStep: () => void;
  autosaveDraftSubmission: (
    formData: ISubmissionFormData,
    submissionId: string,
    draftStep?: number
  ) => Promise<unknown>;
  saveDraftSubmission: (
    formData: ISubmissionFormData,
    submissionId?: string
  ) => void;
  journalName: string;
  stepConfigs: unknown;
  goToStep: (step: number) => void;
}

const PreferenceStep: React.FC<Props> = ({
  currentStep,
  totalSteps,
  draftSubmissionId,
  autosaveDraftSubmission,
  goToNextStep,
  goToPreviousStep,
  saveDraftSubmission,
  journalName,
  stepConfigs,
  goToStep
}) => {
  useEffect(() => scrollToPendingFileField('preference'), []);
  const [isNavigating, setIsNavigating] = useState<boolean>(false);
  const [hasInvalidFundingGrantNumbers, setHasInvalidFundingGrantNumbers] =
    useState(false);
  const [
    hasInvalidFundingGenericGrantNumbers,
    setHasInvalidFundingGenericGrantNumbers
  ] = useState(false);
  const { formData, updateFormData } = useFormContext();
  const ctxId = useCurrentContextId() ?? '';

  const configs = stepConfigs as IPreferenceStepConfig;

  // The confirmations card carries one heading but is filled by whichever of
  // these the article type configures, so it can only be rendered when at least
  // one of them exists - otherwise it drew an empty box under the last field.
  const hasConfirmations = Boolean(
    configs.ManuscriptOwnership ||
      configs.BlindedReviewConsent ||
      configs.ApcAgreement ||
      configs.OpenAccessPublishingConsent ||
      configs.EthicsChecklist ||
      configs.OverlappingMaterial ||
      configs.BlindedPeerReviewConsent ||
      configs.OpenAccessOnly ||
      configs.AuthorDeclaration
  );

  const { submissionContext } = useSubmissionContextData();
  const submissionPreferences =
    submissionContext?.config.cms.authorSubmissionForm.submissionPreferences;

  if (!submissionPreferences) return <h1>missing form Information</h1>;

  const schema = applyRichTextSchemaKeywords(generateValidationSchema(configs));

  const methods = useForm<ISubmissionFormData>({
    defaultValues: formData,
    resolver: ajvResolver(schema, { keywords: RICH_TEXT_AJV_KEYWORDS }),
    mode: 'onChange'
  });
  const isDirty = methods.formState.isDirty;
  const { handleSubmit, getValues, clearErrors, setError, trigger, setValue } =
    methods;

  const onSaveDraft = () => {
    saveDraftSubmission(getValues(), draftSubmissionId);
  };

  const saveCurrentProgress = useCallback(async () => {
    const values = getValues();
    updateFormData(values);
    const results: any = await autosaveDraftSubmission(
      values,
      draftSubmissionId,
      currentStep + 2
    );

    if (results?.response?.isSuccess && results?.response?.draft) {
      updateFormData(results.response.draft);
    }

    return results?.response?.isSuccess;
  }, [autosaveDraftSubmission, currentStep, draftSubmissionId, getValues, updateFormData]);

  const onNavigateSave = () => {
    void saveCurrentProgress();
  };

  const [hasRecommendedConflicts, setHasRecommendedConflicts] = useState(false);
  const [hasOpposedConflicts, setHasOpposedConflicts] = useState(false);

  const hasAnyConflicts = hasRecommendedConflicts || hasOpposedConflicts;
  useSubmissionLeaveGuardRegistration({
    hasUnsavedChanges: isDirty,
    saveCurrentProgress
  });

  const onSubmit = async (data: ISubmissionFormData) => {
    if (hasInvalidFundingGrantNumbers || hasInvalidFundingGenericGrantNumbers) {
      scrollToFirstError();
      return;
    }

    const duplicateIndex = hasDuplicateFundingBody(data);
    if (duplicateIndex !== -1) {
      setError(
        `metadata.fundingGeneric.value.${duplicateIndex}.fundingBody`,
        {
          message: 'Duplicate Funder not allowed',
          type: 'manual' as const
        },
        { shouldFocus: true }
      );
      return;
    }
    // Prevent multiple clicks
    if (isNavigating) return;

    setIsNavigating(true);

    try {
      if (!validateAIToolsData(data)) {
        scrollToFirstError();
        return; // stop submission if invalid
      }
      if (!hasAnyConflicts) {
        const values = getValues();
        updateFormData(values);
        await autosaveDraftSubmission(values, draftSubmissionId);
        goToNextStep();
      } else {
        scrollToFirstError();
      }
    } finally {
      setIsNavigating(false);
    }
  };

  const checkFileIsUploading = () => {
    const values = getValues();
    return (
      Object.keys(values?.fileSet || {}).length > 0 &&
      checkFileUpload?.(values.fileSet)
    );
  };

  const isFileUploading = checkFileIsUploading();

  const validateAIToolsData = (data: ISubmissionFormData): boolean => {
    let isValid = true;

    // Clear any existing errors for AI Tools
    clearErrors(['metadata.aiTools']);

    const aiTools = data?.metadata?.aiTools;
    const exists = aiTools?.exists;
    const value = aiTools?.value;

    // Validation: if "exists" is true, then "value" must have at least one item
    if (exists && (!Array.isArray(value) || value.length === 0)) {
      methods.setError('metadata.aiTools', {
        type: 'manual',
        message: 'Please select at least one option.'
      });
      isValid = false;
    }

    return isValid;
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit, scrollToFirstError)}>
        <SidebarLayout
          sidebar={
            <SubmissionSidebar
              currentStep={currentStep}
              onNavigate={step => {
                goToStep(step);
                onNavigateSave();
              }}
            />
          }
        >
          <StepHeader
            currentStep={currentStep}
            totalSteps={totalSteps}
            heading={submissionPreferences.header}
            description={submissionPreferences.subHeader}
          />
          <Flex
            flexDirection="column"
            gap={4}
            alignItems="flex-start"
            width="100%"
          >
            {configs.CommentForEditor && (
              <OutlineCard width="100%" padding={4}>
                <GenericRichTextInput config={configs.CommentForEditor} />
              </OutlineCard>
            )}

            {configs.CoAuthorApproval && (
              <RadioGroup config={configs.CoAuthorApproval} />
            )}
            {/* ----- Data Availability ----- */}
            {configs.DataAvailability &&
              (configs.DataAvailabilityOpenlyAvailableUrl ||
                (configs.DataAvailabilityEmbargoUrl &&
                  configs.DataAvailabilityEmbargoDate) ||
                configs.DataUnavailabilityReason) && (
                <DataAvailability
                  config={configs.DataAvailability}
                  DataAvailabilityOpenlyAvailableUrlConfig={
                    configs.DataAvailabilityOpenlyAvailableUrl ?? undefined
                  }
                  DataAvailabilityEmbargoUrlConfig={
                    configs.DataAvailabilityEmbargoUrl ?? undefined
                  }
                  DataAvailabilityEmbargoDateConfig={
                    configs.DataAvailabilityEmbargoDate ?? undefined
                  }
                  DataUnavailabilityReasonConfig={
                    configs.DataUnavailabilityReason ?? undefined
                  }
                />
              )}

            {configs.DataAvailabilityStatement && (
              <DataAvailabilityStatement
                config={configs.DataAvailabilityStatement}
                DataAvailabilityTextConfig={
                  configs.DataAvailabilityText ?? null
                }
              />
            )}

            {configs.NoveltyStatement && (
              <NoveltyStatement config={configs.NoveltyStatement} />
            )}

            {/* Figures and Tables */}
            {configs.FiguresAndTables && (
              <FiguresAndTables
                fieldConfig={configs.FiguresAndTables}
                submissionContextId={
                  formData.submissionContext.submissionContextId
                }
              />
            )}

            {/* ----- Licenses and Permissions ----- */}
            {configs.LicensesAndPermissions && (
              <Flex flexDirection="column" gap={4} width="100%">
                <Box>
                  <Text textStyle="headingGroup">
                    {submissionPreferences.licensesAndPermissions.mainLabel}
                  </Text>
                  <Text>
                    {
                      submissionPreferences.licensesAndPermissions
                        .secondaryLabel
                    }
                  </Text>
                </Box>

                {configs.LicensesAndPermissionsFiles &&
                  configs.LicensesAndPermissionsReproducedContent && (
                    <LicensesAndPermissionForm
                      draftSubmissionId={draftSubmissionId}
                      LicensesAndPermissionsConfig={
                        configs.LicensesAndPermissions
                      }
                      LicensesAndPermissionsFilesConfig={
                        configs.LicensesAndPermissionsFiles
                      }
                      LicensesAndPermissionsReproducedContentConfig={
                        configs.LicensesAndPermissionsReproducedContent
                      }
                    />
                  )}
              </Flex>
            )}

            {/* ----- Peer Review ----- */}
            {(configs.TransparentPeerReview ||
              configs.DoubleAnonymousPeerReview ||
              configs.RecommendedReviewers ||
              configs.OpposedReviewers) && (
              <Flex flexDirection="column" gap={4} width="100%">
                {/* <Box>
                  <Text textStyle="headingGroup">
                    {submissionPreferences.peerReviewPreferences.mainLabel}
                  </Text>
                  <Text>
                    {submissionPreferences.peerReviewPreferences.secondaryLabel}
                  </Text>
                </Box> */}

                {/* ----- Double Anonymous Peer Review ----- */}
                {configs.DoubleAnonymousPeerReview &&
                  configs.AnonymizedManuscript && (
                    <DoubleAnonymousReview
                      submissionId={draftSubmissionId}
                      existsConfig={configs.DoubleAnonymousPeerReview}
                      valueConfig={configs.AnonymizedManuscript}
                    />
                  )}

                {/* Recommended Reviewers */}
                {configs.RecommendedReviewers && (
                  <ReviewerRecommendation
                    draftId={draftSubmissionId}
                    ctxId={ctxId}
                    lexiconProjectId={
                      submissionContext?.config.lexiconProjectId ?? ''
                    }
                    config={
                      configs.RecommendedReviewers as FormFieldRecommendedReviewers
                    }
                    onConflictChange={setHasRecommendedConflicts}
                  />
                )}

                {/* Opposed Reviewers */}
                {configs.OpposedReviewers && (
                  <ReviewerRecommendation
                    draftId={draftSubmissionId}
                    ctxId={ctxId}
                    lexiconProjectId={
                      submissionContext?.config.lexiconProjectId ?? ''
                    }
                    config={configs.OpposedReviewers}
                    onConflictChange={setHasOpposedConflicts}
                    skipAffiliationCheck={true}
                  />
                )}

                {/* ----- Transparent Peer Review ----- */}
                {configs.TransparentPeerReview && (
                  <TransparentPeerReview
                    config={configs.TransparentPeerReview}
                  />
                )}
              </Flex>
            )}

            {/* ----- Ethics ----- */}
            {configs.EthicsStatement && (
              <EthicsStatement config={configs.EthicsStatement} />
            )}

            {/* ----- Clinical Trials ----- */}
            {configs.ClinicalTrials && (
              <ClinicalTrials config={configs.ClinicalTrials} />
            )}

            {/* ----- Funding ----- */}
            {configs.Funding && (
              <Funding
                config={configs.Funding}
                onGrantNumbersInvalidChange={setHasInvalidFundingGrantNumbers}
              />
            )}

            {/* ----- Funding Generic ----- */}
            {configs.FundingGeneric && (
              <FundingGeneric
                config={configs.FundingGeneric}
                onGrantNumbersInvalidChange={
                  setHasInvalidFundingGenericGrantNumbers
                }
              />
            )}

            {/* ----- Publishing Model ----- */}
            {configs.PublishingModel && (
              <PublishingModel config={configs.PublishingModel}>
                {configs.OpenAccessChoice && (
                  <Box marginTop={-8}>
                    <RadioGroup config={configs.OpenAccessChoice} />
                  </Box>
                )}
              </PublishingModel>
            )}

            <TextInput
              config={configs.OpenAccessChoiceWorldBankACountry}
              placeholder="Enter your country or region"
            />

            <TextInput
              config={configs.OpenAccessChoiceWorldBankBCountry}
              placeholder="Enter your country or region"
            />

            <TextInput
              config={configs.OpenAccessChoiceReviewerDiscountRate}
              placeholder="Enter an ID"
            />

            {/* ----- Immediate Publication Consent ----- */}
            {configs.PreferenceImmediatePublication && (
              <ImmediatePublication
                config={configs.PreferenceImmediatePublication}
              />
            )}

            {/* ----- Additional Preferences ----- */}
            {(configs.AcceleratedPublication ||
              configs.IPEMMembership ||
              configs.SubjectArea) && (
              <Flex flexDirection="column" gap={4} width="100%">
                <Text textStyle="headingGroup">
                  {submissionPreferences.additionalPreferences.mainLabel}
                </Text>
                <Text>
                  {submissionPreferences.additionalPreferences.secondaryLabel}
                </Text>

                {configs.AcceleratedPublication && (
                  <AcceleratedPublication
                    config={configs.AcceleratedPublication}
                  />
                )}

                {configs.IPEMMembership && (
                  <TextInput
                    config={configs.IPEMMembership}
                    placeholder="eg., SOMEID9999"
                  />
                )}

                {configs.SubjectArea && (
                  <SubjectArea config={configs.SubjectArea} />
                )}
              </Flex>
            )}
            {configs.PreviouslySubmitted && (
              <RadioGroupWithTextArea
                config={configs.PreviouslySubmitted}
                placeholder="Enter manuscript ID"
                setValue={setValue}
              />
            )}

            {configs.ConflictOfInterestForAuthor && (
              <RadioGroupWithTextArea
                config={configs.ConflictOfInterestForAuthor}
                placeholder="Enter conflict of interest"
                setValue={setValue}
              />
            )}

            {configs.AITools && <AITools config={configs.AITools} />}

            {configs.Preprint && (
              <RadioGroupWithTextArea
                config={configs.Preprint}
                placeholder="Enter preprint link"
                setValue={setValue}
              />
            )}

            {configs.SharedDataset && (
              <RadioGroupWithTextArea
                config={configs.SharedDataset}
                placeholder="Enter relevant extant publications"
                setValue={setValue}
              />
            )}

            {(configs.GeneratedDataset || configs.UtilizedDataset) && (
              <OutlineCard padding={4} width="100%">
                <Flex flexDirection="column" gap={4} id="Datasets Statement">
                  <FieldLabel
                    label="Datasets Statement"
                    subText="In the event of acceptance, this text/information will be published: enter it accurately, carefully and completely."
                    isRequired
                  />
                  {configs.GeneratedDataset && (
                    <GeneratedDataset
                      config={configs.GeneratedDataset}
                      hideLabel
                    />
                  )}
                  {configs.UtilizedDataset && (
                    <UtilizedDataset
                      config={configs.UtilizedDataset}
                      hideLabel
                    />
                  )}
                </Flex>
              </OutlineCard>
            )}

            {configs.EthicalApproval && (
              <RadioGroupWithTextArea
                config={configs.EthicalApproval}
                placeholder="Enter ethical approval"
                setValue={setValue}
              />
            )}
            {configs.EthicsApproval && (
              <RadioGroupWithTextArea
                config={configs.EthicsApproval}
                placeholder="Enter ethics approval"
                setValue={setValue}
              />
            )}

            {configs.CopyrightLicensingForm && (
              <OutlineCard width="100%" padding={4}>
                <CustomFormFieldComponent
                  config={configs.CopyrightLicensingForm}
                />
              </OutlineCard>
            )}

            <Flex flexDirection="column" gap={8} width="100%">
              {hasConfirmations && (
                <OutlineCard padding={4}>
                  <Flex flexDirection="column" gap={4}>
                    <FieldLabel label="Confirmations" subText="" isRequired />

                    {configs.ManuscriptOwnership && (
                      <CustomFormFieldComponent
                        config={configs.ManuscriptOwnership}
                      />
                    )}

                    {configs.BlindedReviewConsent && (
                      <CustomFormFieldComponent
                        config={configs.BlindedReviewConsent}
                      />
                    )}

                    {configs.ApcAgreement && (
                      <CustomFormFieldComponent config={configs.ApcAgreement} />
                    )}

                    {/* --- Open Access Only ---- */}
                    {configs.OpenAccessOnly && (
                      <OpenAccessOnly config={configs.OpenAccessOnly} />
                    )}

                    {configs.OpenAccessPublishingConsent && (
                      <CustomFormFieldComponent
                        config={configs.OpenAccessPublishingConsent}
                      />
                    )}

                    {configs.EthicsChecklist && (
                      <CustomFormFieldComponent
                        config={configs.EthicsChecklist}
                      />
                    )}

                    {configs.OverlappingMaterial && (
                      <CustomFormFieldComponent
                        config={configs.OverlappingMaterial}
                      />
                    )}

                    {configs.BlindedPeerReviewConsent && (
                      <CustomFormFieldComponent
                        config={configs.BlindedPeerReviewConsent}
                      />
                    )}

                    {configs.AuthorDeclaration && (
                      <AuthorDeclaration
                        config={configs.AuthorDeclaration}
                        journalName={journalName}
                      />
                    )}
                  </Flex>
                </OutlineCard>
              )}
            </Flex>

            <Flex flexDirection="column" gap={4} alignItems="flex-start"></Flex>
          </Flex>
        </SidebarLayout>
        {/* ----- Footer Buttons ----- */}
        <Flex
          padding={{ base: 3, md: 4 }}
          bottom={0}
          zIndex={1}
          justifyContent="center"
          position="sticky"
          bgColor="surfacePrimaryFill"
          marginX="auto"
        >
          <Flex
            width="100%"
            justifyContent="space-between"
            direction={{ base: 'column', md: 'row' }}
            gap={{ base: 2, md: 0 }}
          >
            <Button
              variant="secondary"
              onClick={async () => {
                const isValid = await trigger();
                if (isValid) {
                  goToPreviousStep();
                  onNavigateSave();
                }
              }}
              isDisabled={isFileUploading}
              width={{ base: '100%', md: 'auto' }}
            >
              <FontAwesomeIcon icon={faChevronLeft} />
              Back
            </Button>
            <Flex
              gap={{ base: 2, md: 4 }}
              direction={{ base: 'column', md: 'row' }}
              width={{ base: '100%', md: 'auto' }}
            >
              {draftSubmissionId && (
                <DeleteDraft
                  submissionId={draftSubmissionId}
                  submissionContextId={ctxId}
                  isDisabled={isFileUploading}
                />
              )}
              <Button
                variant="secondary"
                leftIcon={<FontAwesomeIcon icon={faSave} />}
                onClick={() => onSaveDraft()}
                disabled={isFileUploading || isNavigating}
                width={{ base: '100%', md: 'auto' }}
              >
                Save Draft
              </Button>
              <Button
                variant="primary"
                type="submit"
                disabled={isFileUploading || isNavigating}
                width={{ base: '100%', md: 'auto' }}
              >
                Next
                <FontAwesomeIcon icon={faChevronRight} />
              </Button>
            </Flex>
          </Flex>
        </Flex>
      </form>
    </FormProvider>
  );
};

export default PreferenceStep;
